import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, getDayName } from '../firebase';

function Analytics() {
  const [stats, setStats] = useState({
    totalQuestions: 0,
    uniqueQuestions: 0,
    totalVotes: 0,
    votesByHour: {},
    votesByDay: {},
    topQuestions: [],
    loading: true
  });

  useEffect(() => {
    loadAnalytics();
  }, []);

  const aggregateQuestions = (questions) => {
    const grouped = {};
    
    questions.forEach(q => {
      // Use normalizedText if available, otherwise normalize the text
      const normalizeText = (text) => {
        return text
          .toLowerCase()
          .trim()
          .replace(/[^\w\s]/g, '')
          .replace(/\s+/g, ' ');
      };
      
      const key = q.normalizedText || normalizeText(q.text);
      
      if (!grouped[key]) {
        grouped[key] = {
          text: q.text,  // Use most recent version of text
          normalizedText: key,
          votesA: 0,
          votesB: 0,
          totalVotes: 0,
          timesAsked: 0,
          createdAt: q.createdAt,
          instances: []
        };
      }
      
      grouped[key].votesA += (q.votesA || 0);
      grouped[key].votesB += (q.votesB || 0);
      grouped[key].totalVotes += (q.totalVotes || 0);
      grouped[key].timesAsked += (q.timesAsked || 1);
      grouped[key].instances.push(q.id);
      
      // Keep the most recent createdAt
      if (q.createdAt && (!grouped[key].createdAt || q.createdAt > grouped[key].createdAt)) {
        grouped[key].createdAt = q.createdAt;
      }
    });
    
    return Object.values(grouped);
  };

  const loadAnalytics = async () => {
    try {
      const questionsSnapshot = await getDocs(collection(db, 'questions'));
      const questions = [];
      questionsSnapshot.forEach(doc => {
        questions.push({ id: doc.id, ...doc.data() });
      });

      const votesSnapshot = await getDocs(collection(db, 'votes'));
      const votes = [];
      votesSnapshot.forEach(doc => {
        votes.push(doc.data());
      });

      const votesByHour = {};
      const votesByDay = {};
      
      votes.forEach(vote => {
        if (vote.votedAt) {
          const hour = vote.votedAt.hour;
          votesByHour[hour] = (votesByHour[hour] || 0) + 1;
          
          const day = vote.votedAt.dayOfWeek;
          votesByDay[day] = (votesByDay[day] || 0) + 1;
        }
      });

      // Aggregate questions by normalized text
      const aggregatedQuestions = aggregateQuestions(questions);
      
      const topQuestions = aggregatedQuestions
        .sort((a, b) => (b.totalVotes || 0) - (a.totalVotes || 0))
        .slice(0, 10);

      // Count active questions
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const activeCount = questions.filter(q => {
        if (!q.createdAt) return false;
        const created = q.createdAt.toDate();
        return created > twentyFourHoursAgo;
      }).length;

      setStats({
        totalQuestions: questions.length,
        uniqueQuestions: aggregatedQuestions.length,
        activeQuestions: activeCount,
        totalVotes: votes.length,
        votesByHour,
        votesByDay,
        topQuestions,
        loading: false
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  if (stats.loading) {
    return (
      <div className="min-h-screen bg-atc-purple flex items-center justify-center">
        <div className="text-atc-yellow text-2xl font-bold animate-pulse">Loading Analytics...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-atc-purple">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-atc-yellow mb-8">Ask the Class - Analytics Dashboard</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <h3 className="text-xl font-bold text-atc-purple mb-2">Unique Questions</h3>
            <p className="text-5xl font-bold text-atc-yellow">{stats.uniqueQuestions}</p>
            <p className="text-sm text-gray-500 mt-2">{stats.totalQuestions} total instances</p>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <h3 className="text-xl font-bold text-atc-purple mb-2">Total Votes</h3>
            <p className="text-5xl font-bold text-atc-yellow">{stats.totalVotes}</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg">
            <h3 className="text-xl font-bold text-atc-purple mb-2">Active Now</h3>
            <p className="text-5xl font-bold text-green-600">{stats.activeQuestions || 0}</p>
            <p className="text-sm text-gray-500 mt-2">
              {stats.totalQuestions - (stats.activeQuestions || 0)} expired
            </p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg">
            <h3 className="text-xl font-bold text-atc-purple mb-2">Avg Votes/Question</h3>
            <p className="text-5xl font-bold text-atc-yellow">
              {stats.uniqueQuestions > 0 ? Math.round(stats.totalVotes / stats.uniqueQuestions) : 0}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-lg mb-8">
          <h3 className="text-2xl font-bold text-atc-purple mb-4">Votes by Hour of Day</h3>
          <div className="space-y-2">
            {Object.entries(stats.votesByHour)
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .map(([hour, count]) => {
                const maxVotes = Math.max(...Object.values(stats.votesByHour));
                const percentage = (count / maxVotes) * 100;
                return (
                  <div key={hour} className="flex items-center gap-4">
                    <span className="w-20 text-gray-600 font-mono">
                      {hour.toString().padStart(2, '0')}:00
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative">
                      <div 
                        className="bg-atc-purple rounded-full h-8 flex items-center justify-end pr-3"
                        style={{ width: `${percentage}%` }}
                      >
                        <span className="text-white font-bold text-sm">{count}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          {Object.keys(stats.votesByHour).length === 0 && (
            <p className="text-gray-500 text-center py-8">No vote data yet</p>
          )}
        </div>

        <div className="bg-white rounded-lg p-6 shadow-lg mb-8">
          <h3 className="text-2xl font-bold text-atc-purple mb-4">Votes by Day of Week</h3>
          <div className="space-y-2">
            {[0, 1, 2, 3, 4, 5, 6].map(day => {
              const count = stats.votesByDay[day] || 0;
              const maxVotes = Math.max(...Object.values(stats.votesByDay), 1);
              const percentage = (count / maxVotes) * 100;
              return (
                <div key={day} className="flex items-center gap-4">
                  <span className="w-24 text-gray-600 font-semibold">
                    {getDayName(day)}
                  </span>
                  <div className="flex-1 bg-gray-200 rounded-full h-8 relative">
                    <div 
                      className="bg-atc-yellow rounded-full h-8 flex items-center justify-end pr-3"
                      style={{ width: `${percentage}%` }}
                    >
                      <span className="text-atc-purple font-bold text-sm">{count}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-lg">
          <h3 className="text-2xl font-bold text-atc-purple mb-4">Top 10 Questions by Votes (Aggregated)</h3>
          <div className="space-y-4">
            {stats.topQuestions.map((q, index) => {
              const totalVotes = q.totalVotes || 0;
              const percentA = totalVotes > 0 ? Math.round((q.votesA / totalVotes) * 100) : 0;
              const timesAsked = q.timesAsked || 1;
              return (
                <div key={q.normalizedText || q.text} className="border-b border-gray-200 pb-4 last:border-0">
                  <div className="flex items-start gap-4">
                    <span className="text-3xl font-bold text-atc-yellow">
                      #{index + 1}
                    </span>
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-lg font-semibold text-gray-800 flex-1">{q.text}</p>
                        {timesAsked > 1 && (
                          <span className="text-sm font-bold text-orange-600 bg-orange-100 px-3 py-1 rounded-full ml-3 whitespace-nowrap">
                            🔥 Asked {timesAsked}x
                          </span>
                        )}
                      </div>
                      <div className="flex gap-4 text-sm text-gray-600">
                        <span>👍 YES: {q.votesA} ({percentA}%)</span>
                        <span>👎 NO: {q.votesB} ({100 - percentA}%)</span>
                        <span className="font-bold text-atc-purple">Total: {totalVotes}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {stats.topQuestions.length === 0 && (
            <p className="text-gray-500 text-center py-8">No questions yet</p>
          )}
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={loadAnalytics}
            className="bg-atc-yellow hover:bg-yellow-400 text-atc-purple font-bold py-3 px-8 rounded-lg text-lg transition-colors"
          >
            Refresh Data
          </button>
        </div>
      </div>
    </div>
  );
}

export default Analytics;